/**
 * Whether the user may enter the authenticated app (login redirect + private routes).
 * Requires at least one capability flag or scoped event access from an invite (allowedEventIds).
 */
import { AllowedEventIdsSchema } from '../utils/dbSchemas';

/**
 * Returns true if allowedEventIds grants access to at least one event.
 * Accepts "all" (access all events) or a non-empty JSON string array of event IDs.
 * Empty string, null, undefined, or a malformed value all return false.
 */
export function userHasMeaningfulAllowedEventIds(allowedEventIds: string | null | undefined): boolean {
    const result = AllowedEventIdsSchema.safeParse(allowedEventIds);
    if (!result.success) return false;
    const value = result.data;
    if (value == null || value === '' || value === undefined) return false;
    if (value === 'all') return true;
    try {
        const ids: unknown = JSON.parse(value);
        return Array.isArray(ids) && ids.length > 0;
    } catch {
        return false;
    }
}

type UserRowForAppAccess = {
    admin?: boolean;
    editMapStationsNotes?: boolean;
    createEditEventInfo?: boolean;
    viewWasteData?: boolean;
    editWasteData?: boolean;
    generateReports?: boolean;
    editTodos?: boolean;
    completeTodos?: boolean;
    viewInventory?: boolean;
    manageInventory?: boolean;
    editUsers?: boolean;
    allowedEventIds?: string | null;
};

export function userHasAppAccess(user: UserRowForAppAccess | null | undefined): boolean {
    if (!user) {
        return false;
    }
    const hasAnyPermission =
        user.admin ||
        user.editMapStationsNotes ||
        user.createEditEventInfo ||
        user.viewWasteData ||
        user.editWasteData ||
        user.generateReports ||
        user.editTodos ||
        user.completeTodos ||
        user.viewInventory ||
        user.manageInventory ||
        user.editUsers;
    if (hasAnyPermission) {
        return true;
    }
    return userHasMeaningfulAllowedEventIds(user.allowedEventIds);
}
